import { parseCost, type Cost, type ResourceId } from './resources.js';

export type FacilityCategory = 'government' | 'infrastructure' | 'military' | 'industrial';

/**
 * Onde pode ser construída:
 * - consolidated: só em território consolidado;
 * - occupied: também em território ocupado;
 * - engineers: em ocupado, desde que haja engenheiros no território.
 */
export type FacilityRequirement = 'consolidated' | 'occupied' | 'engineers';

export interface FacilityDef {
  id: string;
  name: string;
  category: FacilityCategory;
  description: string;
  maxLevel: number;
  baseCost: Cost;
  /** Tempo base em minutos (tempo real). */
  baseMinutes: number;
  /** Tempo em turnos (Palácio do Governo). Quando definido, substitui baseMinutes. */
  baseTurns?: number;
  research: string | null;
  allowedIn: FacilityRequirement;
  coastalOnly?: boolean;
  capitalOnly?: boolean;
  /** Única por governo. */
  unique?: boolean;
  /** Máximo por governo. */
  maxPerPlayer?: number;
  /** Resistência por nível contra bombardeio. */
  bombingResistance: number;
  /** Instalação militar: sua existência aparece para vizinhos de fronteira. */
  military?: boolean;
  /** Indústria: insumo produzido, matéria-prima consumida e produção máxima por nível. */
  industry?: { output: ResourceId; input: ResourceId; ratio: number; perLevel: number };
  /** Produção própria, sem insumo: recurso gerado por nível a cada turno. */
  yield?: { output: ResourceId; perLevel: number };
}

type FacilityInput = Omit<FacilityDef, 'baseCost'> & { baseCost: string };

function f(input: FacilityInput): FacilityDef {
  return { ...input, baseCost: parseCost(input.baseCost) };
}

const H = 60;

export const FACILITIES: readonly FacilityDef[] = [
  // ─── Governo (só em território consolidado) ─────────────────────────
  f({
    id: 'palace',
    name: 'Palácio do Governo',
    category: 'government',
    maxLevel: 1,
    description: 'Sede do governo; define a capital. Único, sem níveis. +20% de DEF.',
    baseCost: '500 MAD · 200 AÇO · 1.000 $',
    baseMinutes: 0,
    baseTurns: 5,
    research: null,
    allowedIn: 'consolidated',
    unique: true,
    bombingResistance: 30,
  }),
  f({
    id: 'general-staff',
    name: 'Estado-Maior',
    category: 'government',
    maxLevel: 5,
    description: 'Só na capital. Nível = ordens especiais por turno.',
    baseCost: '200 MAD · 100 AÇO · 400 $',
    baseMinutes: 6 * H,
    research: 'general-staff',
    allowedIn: 'consolidated',
    capitalOnly: true,
    bombingResistance: 30,
  }),
  f({
    id: 'regional-administration',
    name: 'Administração regional',
    category: 'government',
    maxLevel: 5,
    description: '+10% de impostos por nível; +2 de Controle por nível nos ocupados adjacentes.',
    baseCost: '150 MAD · 50 FER · 200 $',
    baseMinutes: 3 * H,
    research: 'administration',
    allowedIn: 'consolidated',
    bombingResistance: 30,
  }),
  f({
    id: 'research-center',
    name: 'Centro de pesquisa',
    category: 'government',
    maxLevel: 5,
    description: '+1 pesquisa simultânea (máx. 3 centros); −10% de tempo de pesquisa por nível.',
    baseCost: '200 MAD · 50 AÇO · 300 $',
    baseMinutes: 4 * H,
    research: null,
    allowedIn: 'consolidated',
    maxPerPlayer: 3,
    bombingResistance: 30,
  }),
  f({
    id: 'intelligence-agency',
    name: 'Agência de inteligência',
    category: 'government',
    maxLevel: 5,
    description: 'Produz agentes, dá acesso à Inteligência e dá contraespionagem.',
    baseCost: '150 MAD · 50 AÇO · 250 $',
    baseMinutes: 4 * H,
    research: 'secret-service',
    allowedIn: 'consolidated',
    bombingResistance: 30,
  }),
  f({
    id: 'state-press',
    name: 'Imprensa estatal',
    category: 'government',
    maxLevel: 5,
    description: 'Única. Publica comunicados no portal (1 por turno; 2 a partir do nível 3).',
    baseCost: '100 MAD · 150 $',
    baseMinutes: 2 * H,
    research: 'propaganda',
    allowedIn: 'consolidated',
    unique: true,
    bombingResistance: 30,
  }),

  // ─── Infraestrutura ──────────────────────────────────────────────────
  f({
    id: 'warehouse',
    name: 'Armazém',
    category: 'infrastructure',
    maxLevel: 5,
    description: '+1.000 de capacidade por recurso e protege 10% do estoque contra saque, por nível.',
    baseCost: '100 MAD · 20 FER · 50 $',
    baseMinutes: 1 * H,
    research: null,
    allowedIn: 'engineers',
    bombingResistance: 20,
  }),
  f({
    id: 'port',
    name: 'Porto',
    category: 'infrastructure',
    maxLevel: 5,
    description: 'Transporte marítimo de recursos (origem e destino). Só em território costeiro.',
    baseCost: '150 MAD · 50 AÇO · 150 $',
    baseMinutes: 3 * H,
    research: 'shipbuilding',
    allowedIn: 'engineers',
    coastalOnly: true,
    bombingResistance: 20,
  }),
  f({
    id: 'airport',
    name: 'Aeroporto',
    category: 'infrastructure',
    maxLevel: 5,
    description: 'Transporte aéreo de recursos e unidades (origem e destino).',
    baseCost: '100 MAD · 80 AÇO · 200 $',
    baseMinutes: 4 * H,
    research: 'aviation',
    allowedIn: 'engineers',
    bombingResistance: 20,
  }),
  f({
    id: 'rail-terminal',
    name: 'Terminal ferroviário',
    category: 'infrastructure',
    maxLevel: 5,
    description: 'Transporte ferroviário de recursos e unidades (origem e destino).',
    baseCost: '100 MAD · 100 AÇO · 150 $',
    baseMinutes: 3 * H,
    research: 'railways',
    allowedIn: 'engineers',
    bombingResistance: 20,
  }),

  // ─── Militar ─────────────────────────────────────────────────────────
  f({
    id: 'barracks',
    name: 'Quartel',
    category: 'military',
    maxLevel: 5,
    description: 'Produção de unidades do Exército e de mísseis de cruzeiro para lançadores terrestres.',
    baseCost: '150 MAD · 50 FER · 100 $',
    baseMinutes: 2 * H,
    research: null,
    allowedIn: 'occupied',
    bombingResistance: 30,
    military: true,
  }),
  f({
    id: 'air-base',
    name: 'Base aérea',
    category: 'military',
    maxLevel: 5,
    description:
      'Produção e base de aeronaves e helicópteros (capacidade 10 × nível); missões por turno = nível.',
    baseCost: '150 MAD · 100 AÇO · 300 $',
    baseMinutes: 5 * H,
    research: 'aviation',
    allowedIn: 'consolidated',
    bombingResistance: 30,
    military: true,
  }),
  f({
    id: 'naval-base',
    name: 'Base naval',
    category: 'military',
    maxLevel: 5,
    description: 'Produção de navios e aviação naval; também funciona como porto. Só em território costeiro.',
    baseCost: '150 MAD · 150 AÇO · 300 $',
    baseMinutes: 5 * H,
    research: 'shipbuilding',
    allowedIn: 'consolidated',
    coastalOnly: true,
    bombingResistance: 30,
    military: true,
  }),
  f({
    id: 'fortifications',
    name: 'Fortificações',
    category: 'military',
    maxLevel: 5,
    description: '+10% de DEF por nível.',
    baseCost: '150 MAD · 50 FER · 80 $',
    baseMinutes: 2 * H,
    research: 'fortification',
    allowedIn: 'occupied',
    bombingResistance: 40,
    military: true,
  }),
  f({
    id: 'aa-battery',
    name: 'Bateria antiaérea',
    category: 'military',
    maxLevel: 5,
    description:
      '+5 de CA por nível; no combate aéreo age como unidade com DEF 10 por nível (3× contra AER e HEL).',
    baseCost: '60 AÇO · 100 $',
    baseMinutes: 2 * H,
    research: 'air-defense',
    allowedIn: 'occupied',
    bombingResistance: 30,
    military: true,
  }),
  f({
    id: 'radar',
    name: 'Radar',
    category: 'military',
    maxLevel: 5,
    description: '+10 de CA por nível, só para detecção, no território e metade nos adjacentes.',
    baseCost: '50 AÇO · 150 $',
    baseMinutes: 3 * H,
    research: 'radar',
    allowedIn: 'occupied',
    bombingResistance: 30,
    military: true,
  }),
  f({
    id: 'coastal-defense',
    name: 'Defesa costeira',
    category: 'military',
    maxLevel: 5,
    description: 'Age como unidade com DEF 15 por nível (2× contra NAV) contra ataques vindos do mar.',
    baseCost: '100 AÇO · 150 $',
    baseMinutes: 3 * H,
    research: 'coastal-defense',
    allowedIn: 'consolidated',
    coastalOnly: true,
    bombingResistance: 40,
    military: true,
  }),
  f({
    id: 'missile-defense',
    name: 'Defesa antimíssil',
    category: 'military',
    maxLevel: 5,
    description: '15% de interceptação por nível (máx. 75%) no território; metade nos adjacentes.',
    baseCost: '100 AÇO · 60 SEM · 500 $',
    baseMinutes: 8 * H,
    research: 'missile-defense',
    allowedIn: 'consolidated',
    bombingResistance: 30,
    military: true,
  }),
  f({
    id: 'missile-silo',
    name: 'Silo de mísseis',
    category: 'military',
    maxLevel: 5,
    description: 'Produção e lançamento de mísseis balísticos e ICBM; armazena 2 × nível mísseis.',
    baseCost: '200 AÇO · 50 SEM · 600 $',
    baseMinutes: 8 * H,
    research: 'rockets',
    allowedIn: 'consolidated',
    bombingResistance: 40,
    military: true,
  }),
  f({
    id: 'space-agency',
    name: 'Agência espacial',
    category: 'military',
    maxLevel: 5,
    description: 'Lançamento de satélites e mísseis antissatélite.',
    baseCost: '200 AÇO · 100 ALU · 100 SEM · 800 $',
    baseMinutes: 12 * H,
    research: 'space-program',
    allowedIn: 'consolidated',
    bombingResistance: 30,
    military: true,
  }),

  // ─── Industrial (consolidado, ou ocupado com engenheiros) ───────────
  f({
    id: 'steel-mill',
    name: 'Siderúrgica',
    category: 'industrial',
    maxLevel: 5,
    description: 'Produz aço (2 FER → 1).',
    baseCost: '150 MAD · 50 FER · 120 $',
    baseMinutes: 2 * H,
    research: 'metallurgy',
    allowedIn: 'engineers',
    bombingResistance: 20,
    industry: { output: 'steel', input: 'iron', ratio: 2, perLevel: 20 },
  }),
  f({
    id: 'refinery',
    name: 'Refinaria',
    category: 'industrial',
    maxLevel: 5,
    description: 'Produz combustível (2 PET → 1).',
    baseCost: '100 MAD · 80 FER · 150 $',
    baseMinutes: 3 * H,
    research: 'motorization',
    allowedIn: 'engineers',
    bombingResistance: 20,
    industry: { output: 'fuel', input: 'oil', ratio: 2, perLevel: 20 },
  }),
  f({
    id: 'cannery',
    name: 'Indústria de enlatados',
    category: 'industrial',
    maxLevel: 5,
    description: 'Produz alimento enlatado: +5 ALI por turno por nível, sem consumir matéria-prima.',
    baseCost: '100 MAD · 40 FER · 100 $',
    baseMinutes: 2 * H,
    research: 'canning',
    allowedIn: 'engineers',
    bombingResistance: 20,
    yield: { output: 'food', perLevel: 5 },
  }),
  f({
    id: 'aluminum-smelter',
    name: 'Fundição de alumínio',
    category: 'industrial',
    maxLevel: 5,
    description: 'Produz alumínio (2 BAU → 1).',
    baseCost: '100 MAD · 80 AÇO · 150 $',
    baseMinutes: 3 * H,
    research: 'aluminum',
    allowedIn: 'engineers',
    bombingResistance: 20,
    industry: { output: 'aluminum', input: 'bauxite', ratio: 2, perLevel: 20 },
  }),
  f({
    id: 'semiconductor-fab',
    name: 'Fábrica de semicondutores',
    category: 'industrial',
    maxLevel: 5,
    description: 'Produz semicondutores (2 SIL → 1).',
    baseCost: '150 AÇO · 50 ALU · 400 $',
    baseMinutes: 6 * H,
    research: 'semiconductors',
    allowedIn: 'engineers',
    bombingResistance: 20,
    industry: { output: 'semiconductors', input: 'silicon', ratio: 2, perLevel: 10 },
  }),
  f({
    id: 'enrichment-plant',
    name: 'Usina de enriquecimento',
    category: 'industrial',
    maxLevel: 5,
    description: 'Produz urânio enriquecido (5 URA → 1).',
    baseCost: '200 AÇO · 50 SEM · 800 $',
    baseMinutes: 10 * H,
    research: 'nuclear-physics',
    allowedIn: 'engineers',
    bombingResistance: 20,
    industry: { output: 'enrichedUranium', input: 'uranium', ratio: 5, perLevel: 2 },
  }),
];

export const FACILITY_BY_ID: Readonly<Record<string, FacilityDef>> = Object.fromEntries(
  FACILITIES.map((def) => [def.id, def]),
);
export const FACILITY_IDS: readonly string[] = FACILITIES.map((def) => def.id);

export function getFacility(id: string): FacilityDef {
  const def = FACILITY_BY_ID[id];
  if (!def) throw new Error(`Instalação desconhecida: ${id}`);
  return def;
}

/** Tipos de instalação que viram "alvo" de bombardeio, além do estoque. */
export const BOMBING_TARGET_STOCK = 'stock';
